import { RSVPFormData, RSVPParty, PartyMember } from '../types'

// Google Sheets configuration
const SPREADSHEET_ID = import.meta.env.VITE_GOOGLE_SHEETS_ID
const APPS_SCRIPT_URL = import.meta.env.VITE_GOOGLE_APPS_SCRIPT_URL || 'https://script.google.com/macros/s/YOUR_DEPLOYMENT_ID/exec'

// Browser-compatible Google Sheets API using fetch
const API_KEY = import.meta.env.VITE_GOOGLE_SHEETS_API_KEY
const BASE_URL = 'https://sheets.googleapis.com/v4/spreadsheets'

export interface GoogleSheetsResponse {
  success: boolean
  message?: string
  error?: string
}



// Read all parties from Google Sheets using REST API
export async function fetchPartiesFromSheets(): Promise<RSVPParty[]> {
  try {
    if (!SPREADSHEET_ID) {
      throw new Error('Google Sheets not configured - missing spreadsheet ID')
    }

    if (!API_KEY) {
      throw new Error('Google Sheets API key not configured')
    }

    // Fetch parties data
    const partiesUrl = `${BASE_URL}/${SPREADSHEET_ID}/values/Parties!A2:K1000?key=${API_KEY}`
    const partiesResponse = await fetch(partiesUrl)
    
    if (!partiesResponse.ok) {
      throw new Error(`Failed to fetch parties: ${partiesResponse.status} ${partiesResponse.statusText}`)
    }
    
    const partiesData = await partiesResponse.json()

    // Fetch members data (including Welcome Drinks column I)
    const membersUrl = `${BASE_URL}/${SPREADSHEET_ID}/values/PartyMembers!A2:I1000?key=${API_KEY}`
    const membersResponse = await fetch(membersUrl)
    
    if (!membersResponse.ok) {
      throw new Error(`Failed to fetch members: ${membersResponse.status} ${membersResponse.statusText}`)
    }
    
    const membersData = await membersResponse.json()

    return transformSheetsDataToParties(
      partiesData.values || [], 
      membersData.values || []
    )
  } catch (error) {
    console.error('Error fetching parties from Google Sheets:', error)
    throw error
  }
}

// Transform raw sheets data into RSVPParty objects
function transformSheetsDataToParties(partiesData: string[][], membersData: string[][]): RSVPParty[] {
  const parties: RSVPParty[] = []

  partiesData.forEach(row => {
    if (row.length < 3) return // Skip incomplete rows

    const [
      partyId,
      partyName,
      primaryContactName,
      email,
      phone,
      maxPartySize,
      allowsPlusOnes,
      hasRSVPed,
      rsvpDate,
      rsvpSubmittedBy,
      specialRequests
    ] = row

    // Find all members for this party
    const partyMembers: PartyMember[] = membersData
      .filter(memberRow => memberRow[1] === partyId) // Filter by Party ID
      .map(memberRow => {
        const [
          memberId,
          ,
          name,
          type,
          age,
          isAttending,
          mealChoice,
          notes,
          isAttendingWelcomeDrinks
        ] = memberRow

        return {
          id: memberId || `member-${Date.now()}`,
          name: name || '',
          type: (type as PartyMember['type']) || 'primary',
          age: (age as PartyMember['age']) || 'adult',
          isAttending: isAttending === 'TRUE' || isAttending === 'Yes',
          isAttendingWelcomeDrinks: isAttendingWelcomeDrinks === 'TRUE' || isAttendingWelcomeDrinks === 'Yes' 
            ? true 
            : isAttendingWelcomeDrinks === 'FALSE' || isAttendingWelcomeDrinks === 'No'
            ? false
            : undefined,
          mealChoice: mealChoice as PartyMember['mealChoice'],
          notes: notes || ''
        }
      })

    parties.push({
      id: partyId || `party-${Date.now()}`,
      partyName: partyName || '',
      primaryContact: {
        name: primaryContactName || '',
        email: email || '',
        phone: phone || ''
      },
      members: partyMembers,
      maxPartySize: parseInt(maxPartySize) || partyMembers.length,
      allowsAdditionalPlusOnes: allowsPlusOnes === 'TRUE',
      hasRSVPed: hasRSVPed === 'TRUE',
      rsvpSubmittedAt: rsvpDate ? new Date(rsvpDate) : undefined,
      rsvpSubmittedBy: rsvpSubmittedBy || '',
      specialRequests: specialRequests || ''
    })
  })

  return parties
}


// Update party RSVP in Google Sheets via Apps Script
export async function updatePartyRSVPInSheets(
  party: RSVPParty,
  rsvpData: RSVPFormData
): Promise<void> {
  try {
    // Check if Apps Script is configured
    if (!APPS_SCRIPT_URL || APPS_SCRIPT_URL.includes('YOUR_DEPLOYMENT_ID')) {
      return
    }

    const payload = {
      action: 'updateRSVP',
      partyId: party.id,
      partyName: party.partyName,
      submittedBy: rsvpData.searchName,
      submittedAt: new Date().toISOString(),
      members: rsvpData.partyMembers.map(member => ({
        id: member.id,
        name: member.name,
        isAttending: member.isAttending,
        isAttendingWelcomeDrinks: member.isAttendingWelcomeDrinks,
        mealChoice: member.mealChoice || '',
        notes: member.notes || ''
      })),
      additionalPlusOnes: rsvpData.additionalPlusOnes?.map(plusOne => ({
        name: plusOne.name,
        isAttendingWelcomeDrinks: plusOne.isAttendingWelcomeDrinks,
        mealChoice: plusOne.mealChoice || '',
        notes: plusOne.notes || ''
      })) || [],
      specialRequests: rsvpData.specialRequests || ''
    }


    // Use form data instead of JSON to avoid CORS preflight
    const formData = new FormData()
    formData.append('data', JSON.stringify(payload))

    const response = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Apps Script update error! status: ${response.status}`)
    }

    const result = await response.json()
    if (!result.success) {
      throw new Error(result.error || 'Apps Script update failed')
    }

  } catch (error) {
    console.error('Error updating party RSVP via Apps Script:', error)
    throw error
  }
}

// Apps Script submission for writes (since API key doesn't allow writes)
export async function submitRSVPToGoogleSheets(rsvpData: RSVPFormData, party: RSVPParty): Promise<GoogleSheetsResponse> {
  try {
    // Check if Apps Script is configured
    if (!APPS_SCRIPT_URL || APPS_SCRIPT_URL.includes('YOUR_DEPLOYMENT_ID')) {
      return {
        success: true,
        message: 'RSVP submitted successfully (development mode - no actual write)!'
      }
    }


    // Prepare payload for Apps Script
    const payload = {
      action: 'submitRSVP',
      partyId: party.id,
      partyName: party.partyName,
      primaryContact: party.primaryContact.email || '',
      submittedBy: rsvpData.searchName,
      members: rsvpData.partyMembers.map(member => ({
        id: member.id,
        name: member.name,
        isAttending: member.isAttending,
        isAttendingWelcomeDrinks: member.isAttendingWelcomeDrinks,
        mealChoice: member.mealChoice || '',
        notes: member.notes || ''
      })),
      additionalPlusOnes: rsvpData.additionalPlusOnes?.map(plusOne => ({
        name: plusOne.name,
        isAttendingWelcomeDrinks: plusOne.isAttendingWelcomeDrinks,
        mealChoice: plusOne.mealChoice || '',
        notes: plusOne.notes || ''
      })) || [],
      specialRequests: rsvpData.specialRequests || '',
      submittedAt: new Date().toISOString()
    }


    // Use form data instead of JSON to avoid CORS preflight
    const formData = new FormData()
    formData.append('data', JSON.stringify(payload))

    const response = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      body: formData,
    })


    if (!response.ok) {
      const responseText = await response.text()
      throw new Error(`Apps Script error! status: ${response.status} - ${responseText}`)
    }

    const result = await response.json()
    
    return result
  } catch (error) {
    console.error('Error submitting RSVP to Apps Script:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }
  }
}

// Mock function for development/testing
export async function submitRSVPMock(_rsvpData: RSVPFormData, _party: RSVPParty): Promise<GoogleSheetsResponse> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000))
  
  // Simulate random success/failure for testing
  const success = Math.random() > 0.1 // 90% success rate
  
  if (success) {
    return {
      success: true,
      message: 'RSVP submitted successfully!'
    }
  } else {
    return {
      success: false,
      error: 'Failed to submit RSVP. Please try again.'
    }
  }
}

// Export the appropriate function based on environment
export const submitRSVP = submitRSVPToGoogleSheets 